import React, { memo, useCallback, useRef } from 'react'
import { useParams } from 'react-router'
import { useSortable } from '@dnd-kit/sortable'
import { TtTrayItem, TtTrayHandout, TtButton } from '@23/react'
import {
  useStartSharingAttachment,
  useStopSharingAttachment,
} from './hooks/use-attachments'

import Toolbar from './Toolbar'
import GutterMenu from './GutterMenu'
import useTrayTracking from './useTrayTracking'
import { useScrollRegister, useScroll } from './ScrollProvider'
import { TrayCardHandout } from '../../../firebase/model/tray-cards/types/handout-model'
import { setActiveCard, removeCard } from '../../../firebase/hooks/useTray'

interface HandoutCardProps {
  isActiveCard: boolean
  isPriorToSelectedCard: boolean
  isLastCard: boolean
  position: number
  card: TrayCardHandout
}

const HandoutCard = ({
  isActiveCard,
  isPriorToSelectedCard,
  isLastCard,
  position,
  card,
}: HandoutCardProps): JSX.Element => {
  const trayTracking = useTrayTracking()
  const { live_id: liveId } = useParams<{ live_id: string }>()
  const ref = useRef<HTMLTtTrayItemElement>(null)
  useScrollRegister(card.id, position)
  const { scrollIntoView } = useScroll()
  const { mutate: startSharingAttachment, isLoading: isStartLoading } =
    useStartSharingAttachment(liveId, card.filename)
  const { mutate: stopSharingAttachment, isLoading: isStopLoading } =
    useStopSharingAttachment(liveId, card.filename)

  const { setNodeRef, transform, transition, isDragging, isSorting } =
    useSortable({
      id: card.id,
    })

  const style = isSorting
    ? {
        transform: `translateY(${transform?.y ?? 0}px)`,
        transition,
        position: 'relative' as const,
        zIndex: isDragging ? 1 : undefined,
        opacity: isDragging ? 0.8 : 1,
      }
    : {}

  const onCardClickHandler = () => {
    trayTracking.activated(card.id)
    setActiveCard(card.id)
    scrollIntoView(card.id)
  }

  const onShareClickHandler = () => {
    startSharingAttachment()
  }

  const onStopShareClickHandler = () => {
    stopSharingAttachment()
  }

  const getGutterMode = useCallback(() => {
    if (isSorting) return 'disabled'
    if (isLastCard) return 'persisted'
    return 'normal'
  }, [isLastCard, isSorting])

  setNodeRef(ref.current)

  return (
    <TtTrayItem
      data-id={card.id}
      ref={ref}
      style={style}
      gutterDelay={400}
      gutterMode={getGutterMode()}
    >
      <TtTrayHandout
        filename={card.filename}
        filesize={card.fileSize}
        selected={isActiveCard}
        disabled={isPriorToSelectedCard}
        onTt-click={(event) => {
          if (event.target === event.currentTarget) {
            onCardClickHandler()
          }
        }}
      >
        <Toolbar
          cardId={card.id}
          onDeleteClick={() => {
            trayTracking.deleted(card.id)
            removeCard(card.id)
          }}
        />
        {card.hidden && (
          <TtButton
            color="secondary"
            size="small"
            pill
            loading={isStartLoading}
            onClick={onShareClickHandler}
          >
            Share
          </TtButton>
        )}
        {!card.hidden && (
          <TtButton
            color="secondary"
            variant="outline"
            size="small"
            pill
            loading={isStopLoading}
            onClick={onStopShareClickHandler}
          >
            Stop sharing
          </TtButton>
        )}
      </TtTrayHandout>
      <GutterMenu cardId={card.id} />
    </TtTrayItem>
  )
}

export default memo(HandoutCard)
